import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styles from '../styles/profile-header.module.scss'
import HistoryLineChart from 'components/chart/HistoryLineChart/HistoryLineChart'
import ResultsSummarySection from 'components/chart/ResultsDoughnutChart/ResultsSummarySection'
import TournamentChart from 'components/chart/TournamentChart/TournamentChart'
import InfoBoxesWrapper from 'components/generic/InfoBoxesWrapper'
import PlayerStats from '../PlayerStats'
import { MatchUpTypeEnum, Player } from 'types/global'
import AdditionalInfo from '../AdditionalInfo'
import ProfileStrength from '../ProfileStrength'
import ProfilePanel from '../generic/profile-panel/ProfilePanel'
import { useSelector } from 'react-redux'
import { selectUserIsLoggedIn } from 'redux/selectors'
import PlayerLevel from '../player-level/PlayerLevel'
import BlurredWtnHistoryChart from '../locked-panels/BlurredWtnHistoryChart'

interface Props {
  player: Player
  isOwnProfile: boolean
}

const OverviewTab = ({ player, isOwnProfile }: Props) => {
  const isLoggedIn = useSelector(selectUserIsLoggedIn)
  const playerIds = useMemo(() => [player.id], [player])
  const { t: tProfile } = useTranslation('strProfile')
  const { t: tCharts } = useTranslation('strCharts')

  return (
    <div className={styles.tabContentWrapper}>
      <h1 className={styles.tabContentTitle}>{tProfile('overview')}</h1>

      {isOwnProfile && <ProfileStrength profile={player} />}

      <InfoBoxesWrapper>
        <AdditionalInfo profile={player} />
        <PlayerLevel profile={player} isLoggedIn={isLoggedIn} />
      </InfoBoxesWrapper>

      <PlayerStats playerId={player.id} />

      <ProfilePanel title={tProfile('wtnHistory')}>
        {isLoggedIn ? (
          <HistoryLineChart
            playerIds={playerIds}
            labels={[
              {
                type: MatchUpTypeEnum.SINGLES,
                name: tCharts('singles'),
                keyCol: 0,
              },
              {
                type: MatchUpTypeEnum.DOUBLES,
                name: tCharts('doubles'),
                keyCol: 1,
              },
            ]}
          />
        ) : (
          <BlurredWtnHistoryChart />
        )}
      </ProfilePanel>

      <InfoBoxesWrapper>
        <ProfilePanel title={tProfile('playerTournMatches')}>
          <TournamentChart
            playerId={player.id}
            lastPlayedSingles={player.lastPlayedSingles}
            lastPlayedDoubles={player.lastPlayedDoubles}
          />
        </ProfilePanel>
        <ProfilePanel
          title={tProfile('resultsSummary')}
          classNames={{ container: styles.panel, content: styles.panelContentWithSubtitle }}
        >
          <ResultsSummarySection playerId={player.id} />
        </ProfilePanel>
      </InfoBoxesWrapper>
    </div>
  )
}

export default OverviewTab
